/* ──────────────────────────────────────────────────────────────────────────
 * Live operational model — the thing that makes the platform ACTIVE.
 *
 * Until now every figure was a frozen snapshot. That is the reason several
 * capabilities in the brief could not exist: §5 forecasting has nothing to
 * forecast from if occupancy never moves, §6 playback has no history to
 * replay, and §22 cannot measure prediction accuracy if there is no later
 * actual to compare a prediction against.
 *
 * So this evolves sector state on a clock, and the whole platform reads from
 * it. Crowd builds and drains, arrivals surge around ritual timings and
 * train/bus landings, incidents age, and forecasts are checked against what
 * actually happened.
 *
 * ── WHY A MODEL AND NOT RANDOM NUMBERS ──
 * Random walk would look alive and teach nothing. This uses an explicit
 * arrival model — a base diurnal curve, ritual peaks, scheduled transport
 * landings, and egress limited by gate capacity — so the numbers move for
 * reasons an operator can name, and an intervention that changes a gate rate
 * visibly changes the curve. That is what makes the demo defensible when
 * someone asks "why did it do that".
 *
 * ── AND WHY IT IS STILL SIMULATED ──
 * No sensor is attached. These are plausible dynamics over invented
 * quantities, and every consumer keeps the SIMULATED badge. A model that
 * moves convincingly is more persuasive than a static number, which makes
 * labelling it MORE important here, not less.
 * ────────────────────────────────────────────────────────────────────────── */

import { SECTORS, STATUS_THRESHOLDS, EXIT_GATES } from "../data/kumbhGeo";
import { ASSEMBLY_AREA_M2, bandForDensity } from "./crowdSafety";

/* ── Egress flow ───────────────────────────────────────────────────────
 * Entry and exit are not the same thing and must not share a constant.
 * An entry gate is screened, ticketed and staffed, which is why
 * ASSUMPTIONS.GATE_THROUGHPUT_PER_MIN is only 40 persons/min. An exit is an
 * open corridor with nobody checking anything, and its capacity is set by
 * pedestrian flow physics instead:
 *
 *   ~1.3 persons per metre of width per second in a free-flowing stream
 *   (the standard planning figure), across an assumed 8 m exit corridor
 *   = 1.3 × 8 × 60 ≈ 624 persons/min.
 *
 * Using the entry figure for egress — which this model did until it was
 * caught — makes every sector in the theatre read as a permanent crush
 * precursor, and an alarm that is always on tells a commander nothing.
 */
export const EXIT_CORRIDOR_WIDTH_M = 8;
export const EXIT_FLOW_PER_M_PER_MIN = 1.3 * 60;
export const EXIT_FLOW_PER_MIN = Math.round(
  EXIT_CORRIDOR_WIDTH_M * EXIT_FLOW_PER_M_PER_MIN,
);

/* One simulated minute per tick. The UI ticks faster than real time so a
 * demo can watch an hour unfold; the model itself is expressed in minutes so
 * the arithmetic stays readable. */
export const TICK_MINUTES = 1;

/* Shahi Snan and principal bathing windows drive the sharpest arrival peaks
 * at a Kumbh. Times are minutes past midnight. */
const RITUAL_PEAKS = [
  { at: 5 * 60,  spread: 90,  strength: 2.6, label: { en: "Brahma Muhurta snan", mr: "ब्रह्म मुहूर्त स्नान" } },
  { at: 11 * 60, spread: 120, strength: 1.5, label: { en: "Midday darshan",      mr: "दुपारचे दर्शन" } },
  { at: 18 * 60, spread: 100, strength: 1.9, label: { en: "Evening aarti",       mr: "संध्याकाळची आरती" } },
];

/* Scheduled transport landings. A train arriving is a step change in
 * pedestrian demand 20-40 minutes later at the ghats — the §11 point that
 * mobility and crowd cannot be modelled separately. */
export const TRANSPORT_ARRIVALS = [
  { at: 4 * 60 + 40,  mode: "rail", people: 4200, sector: "SEC-05", lagMin: 35 },
  { at: 7 * 60 + 15,  mode: "rail", people: 3800, sector: "SEC-05", lagMin: 35 },
  { at: 10 * 60 + 5,  mode: "bus",  people: 2600, sector: "SEC-06", lagMin: 25 },
  { at: 16 * 60 + 30, mode: "rail", people: 5100, sector: "SEC-05", lagMin: 35 },
  { at: 17 * 60 + 45, mode: "bus",  people: 3000, sector: "SEC-06", lagMin: 25 },
];

/* Base share of the theatre's arrivals each sector attracts. */
const PULL = {
  "SEC-01": 0.34,  // Ramkund — the principal ghat
  "SEC-02": 0.22,
  "SEC-03": 0.18,  // Trimbakeshwar
  "SEC-04": 0.12,
  "SEC-05": 0.09,
  "SEC-06": 0.05,
};

const gauss = (x, mu, sigma) => Math.exp(-((x - mu) ** 2) / (2 * sigma ** 2));

/* Theatre-wide arrival intensity at a given minute of day, as a multiplier
 * on the baseline rate. Deterministic — the same minute always gives the
 * same intensity, so a demo is reproducible and a forecast can be checked. */
export function arrivalIntensity(minuteOfDay) {
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  // Overnight trough, daytime plateau.
  const diurnal = 0.35 + 0.65 * gauss(m, 12 * 60, 300);
  const ritual = RITUAL_PEAKS.reduce(
    (sum, p) => sum + p.strength * gauss(m, p.at, p.spread), 0
  );
  return diurnal + ritual;
}

/* Extra arrivals landing in a sector this minute from scheduled transport. */
export function transportPulse(minuteOfDay, sectorId) {
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  return TRANSPORT_ARRIVALS.reduce((sum, t) => {
    if (t.sector !== sectorId) return sum;
    const landsAt = t.at + t.lagMin;
    // Spread the cohort over ~15 minutes rather than a single spike.
    return sum + (t.people / 15) * gauss(m, landsAt, 6);
  }, 0);
}

export function ritualContext(minuteOfDay) {
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  const active = RITUAL_PEAKS
    .map((p) => ({ ...p, weight: gauss(m, p.at, p.spread) }))
    .filter((p) => p.weight > 0.35)
    .sort((a, b) => b.weight - a.weight)[0];
  const next = TRANSPORT_ARRIVALS
    .map((t) => ({ ...t, inMin: Math.round(t.at + t.lagMin - m) }))
    .filter((t) => t.inMin > 0)
    .sort((a, b) => a.inMin - b.inMin)[0];
  return { peak: active || null, nextArrival: next || null };
}

/* ── One tick ──────────────────────────────────────────────────────────
 * Inflow is demand-driven; OUTFLOW IS CAPACITY-LIMITED, which is the whole
 * §7 point. A sector cannot drain faster than its exit gates allow, so when
 * arrivals exceed egress capacity the crowd accumulates whatever the
 * operator wishes — and that is the mechanism the Jamarat model exists to
 * prevent. */
export function step(state, {
  baselineRatePerMin = 900,
  /* §6 levers. An intervention changes the model's inputs rather than its
   * outputs, so a simulated intervention is the same arithmetic as the
   * live run and its effect can be compared like for like. Multipliers
   * default to 1, so an unparameterised step is unchanged. */
  inflowMultipliers = null,
  egressMultipliers = null,
  transferIn = null,
} = {}) {
  const minuteOfDay = state.minuteOfDay + TICK_MINUTES;
  const intensity = arrivalIntensity(minuteOfDay);

  const sectors = state.sectors.map((s) => {
    const def = SECTORS.find((x) => x.id === s.id);
    const pull = PULL[s.id] ?? 0.1;

    const inflowMult = inflowMultipliers?.[s.id] ?? 1;
    const egressMult = egressMultipliers?.[s.id] ?? 1;
    /* People diverted out of one sector have to arrive in another. A
     * simulator that removes them from the theatre would show every
     * diversion as a pure win. */
    const diverted = transferIn?.[s.id] ?? 0;

    const inflow =
      (baselineRatePerMin * intensity * pull + transportPulse(minuteOfDay, s.id))
        * inflowMult
      + diverted;

    /* Egress: exit-gate throughput, reduced as the sector gets denser —
     * a packed crowd moves through a gate more slowly, not faster. */
    const area = ASSEMBLY_AREA_M2[s.id] || 30000;
    const density = s.current / area;
    const congestionPenalty = density > 3 ? Math.max(0.35, 1 - (density - 3) * 0.25) : 1;
    /* A sector with no mapped exit still has to drain in the simulation or
     * the population diverges, so it falls back to a single nominal exit —
     * but its ingress/egress ratio is reported as unknown rather than
     * computed from a number nobody surveyed. */
    const lanes = s.exitGates > 0 ? s.exitGates : 1;
    const egressCapacity = lanes * EXIT_FLOW_PER_MIN * congestionPenalty * egressMult;

    /* People only leave if they are there to leave. */
    const outflow = Math.min(egressCapacity, s.current * 0.02);

    const next = Math.max(0, Math.min(def.capacity * 1.15, s.current + inflow - outflow));

    return {
      ...s,
      current: Math.round(next),
      inflowPerMin: Math.round(inflow),
      outflowPerMin: Math.round(outflow),
      netPerMin: Math.round(inflow - outflow),
      egressCapacityPerMin: Math.round(egressCapacity),
      /* §7: the ratio that says whether inflow is compatible with the
       * ability to get people out again. Above 1 and the sector is filling
       * faster than it can ever be emptied. Null where no exit is mapped —
       * an unsurveyed sector must not read as compatible. */
      ingressEgressRatio: s.exitGates > 0
        ? Number((inflow / Math.max(1, egressCapacity)).toFixed(2))
        : null,
    };
  });

  return { minuteOfDay, sectors, tick: state.tick + 1 };
}

/* Initial state, seeded from the static sector definitions so the live model
 * starts where the frozen demo did. */
export function initialState(startMinuteOfDay = 10 * 60 + 30) {
  return {
    minuteOfDay: startMinuteOfDay,
    tick: 0,
    sectors: SECTORS.map((s) => {
      const exitGates = EXIT_GATES.filter((g) => g.sector === s.id).length;
      return {
        id: s.id,
        name: s.name,
        centre: s.centre,
        capacity: s.capacity,
        current: s.current,
        /* Counted from the mapped exit gates, not assumed. A sector with
         * none mapped is a data gap and is reported as one below, rather
         * than quietly given a gate it does not have. */
        exitGates,
        inflowPerMin: 0,
        outflowPerMin: 0,
        netPerMin: 0,
        egressCapacityPerMin: exitGates * EXIT_FLOW_PER_MIN,
        ingressEgressRatio: exitGates > 0 ? 0 : null,
      };
    }),
  };
}

/* ── §5 Multi-horizon forecast ─────────────────────────────────────────
 * Runs the same model forward without mutating live state. Because the
 * model is deterministic, a forecast made now can be compared against what
 * actually happens — which is what makes §22's "prediction accuracy"
 * measurable rather than asserted. */
export const HORIZONS = [5, 15, 30, 60];

export function forecast(state, horizons = HORIZONS, opts = {}) {
  const out = {};
  let cursor = { ...state, sectors: state.sectors.map((s) => ({ ...s })) };
  const want = [...horizons].sort((a, b) => a - b);
  let reached = 0;

  want.forEach((h) => {
    while (reached < h) { cursor = step(cursor, opts); reached += TICK_MINUTES; }
    out[h] = cursor.sectors.map((s) => {
      const area = ASSEMBLY_AREA_M2[s.id] || 30000;
      const density = s.current / area;
      return {
        id: s.id,
        name: s.name,
        centre: s.centre,
        current: s.current,
        occupancyPct: Math.round((s.current / s.capacity) * 100),
        density: Number(density.toFixed(2)),
        band: bandForDensity(density).id,
        status:
          s.current / s.capacity >= STATUS_THRESHOLDS.red ? "red"
          : s.current / s.capacity >= STATUS_THRESHOLDS.amber ? "amber" : "green",
        ingressEgressRatio: s.ingressEgressRatio,
      };
    });
  });
  return out;
}

/* What changes between now and a horizon — the part an operator acts on.
 * Reports only sectors that cross a band, because a list of everything is a
 * list nobody reads. */
export function forecastDeltas(state, horizon = 30, opts = {}) {
  const fc = forecast(state, [horizon], opts)[horizon];
  const nowBand = Object.fromEntries(
    state.sectors.map((s) => {
      const area = ASSEMBLY_AREA_M2[s.id] || 30000;
      return [s.id, bandForDensity(s.current / area).id];
    })
  );

  return fc
    .map((f) => ({ ...f, fromBand: nowBand[f.id] }))
    .filter((f) => f.band !== f.fromBand)
    .map((f) => ({
      ...f,
      direction: f.band > f.fromBand ? "worsening" : "improving",
    }));
}

/* ── §7 Ingress / egress intelligence ──────────────────────────────────
 * The question the Jamarat model is built around: is the rate we are letting
 * people IN compatible with our ability to get them OUT? A sector can look
 * acceptable on occupancy and still be structurally unsafe if the answer is
 * no, because the crowd has no route to disperse. */
export function ingressEgressAssessment(state) {
  return state.sectors
    .map((s) => {
      const ratio = s.ingressEgressRatio;
      const minutesToClear = s.outflowPerMin > 0
        ? Math.round(s.current / s.outflowPerMin)
        : null;
      return {
        id: s.id,
        name: s.name,
        centre: s.centre,
        inflowPerMin: s.inflowPerMin,
        egressCapacityPerMin: s.egressCapacityPerMin,
        ratio,
        /* How long a full evacuation would take at the current egress rate.
         * Null when nothing is leaving — an infinite clearance time is not a
         * number, it is a finding. */
        minutesToClear,
        /* A sector with no mapped exit has no ratio, and therefore no
         * verdict. Calling it compatible would be the worst possible
         * default: it would rank an unsurveyed sector as safe. */
        compatible: ratio == null ? null : ratio <= 1,
        verdict:
          ratio == null ? "unknown"
          : ratio > 1.5 ? "critical"
          : ratio > 1 ? "incompatible"
          : ratio > 0.8 ? "tight" : "ok",
      };
    })
    /* Unknowns sort to the top, not the bottom — a gap in the survey is a
     * finding a commander should see, not one to scroll past. */
    .sort((a, b) => {
      if (a.ratio == null && b.ratio == null) return 0;
      if (a.ratio == null) return -1;
      if (b.ratio == null) return 1;
      return b.ratio - a.ratio;
    });
}

export const LIVE_ASSUMPTIONS = [
  "Arrival intensity is a modelled diurnal curve with ritual peaks, not an observed count.",
  "Transport cohorts land 25–35 minutes after the scheduled arrival; a real figure comes from the corridor walk time.",
  `Egress is limited to ${EXIT_FLOW_PER_MIN} persons/min per mapped exit, derived from ~1.3 persons/m/s across an assumed ${EXIT_CORRIDOR_WIDTH_M} m corridor, and reduced as density rises — a packed crowd clears an exit more slowly, not faster.`,
  "Exit counts come from the mapped exit gates. A sector with none mapped reports its ingress/egress ratio as unknown rather than computing one from a figure nobody surveyed.",
  "Forecasts run the same deterministic model forward, so they are reproducible and checkable — but they inherit every assumption above.",
  "No sensor, CCTV or transport feed is connected. These are plausible dynamics over invented quantities.",
];
